import { useState, useMemo } from "react";
import { Plus, Search, MoreVertical, FileText, Filter, X, Tags } from "lucide-react";
import { useNavigate } from "react-router-dom";

import MenuModal, { Menu } from "../../../components/ui/MenuModal";
import DeleteConfirmationModal from "../../../components/ui/DeleteConfirmationModal";
import PageListLayout from "../../../layouts/PageListLayout";
import FlexContainer from "../../../components/ui/FlexContainer";
import usePosts from "../../../hooks/features/posts/usePosts";
import { alertSuccess, alertError } from "../../../helpers/alertError";
import { Post } from "../../../types/features/Post";
import PostsList from "../../../components/shared/blog/posts/PostsList";
import CategorieModal from "../../../components/shared/blog/categories/CategorieModal";
import { useProduct } from "../../../hooks/core/useProduct";

export default function PostsPage({}) {
  const navigate = useNavigate();
  const role = `admin`;
  const { selectedProduct } = useProduct();

  const { posts, deletePost, deleteManyPosts } = usePosts();
  const [isAddMenuOpen, setIsAddMenuOpen] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  const [postToDelete, setPostToDelete] = useState<Post | null>(null);
  const [itemsToDelete, setItemsToDelete] = useState<string[] | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  // ✅ Articles filtrés par produit (pour les stats)
  const postsByProduct = useMemo(() => {
    if (selectedProduct && selectedProduct !== 'all') {
      return posts.filter((p) => p.product_name === selectedProduct);
    }
    return posts;
  }, [posts, selectedProduct]);

  // ✅ Stats basées sur les articles filtrés par produit
  const stats = useMemo(
    () => ({
      total: postsByProduct.length,
      published: postsByProduct.filter((p) => p.status === "published").length,
      draft: postsByProduct.filter((p) => p.status === "draft").length,
      archived: postsByProduct.filter((p) => p.status === "archived").length,
    }),
    [postsByProduct]
  );

  // ✅ Filtrer les articles (recherche + catégorie + statut) à partir des articles déjà filtrés par produit
  const filteredPosts = useMemo(() => {
    let result = postsByProduct;

    // Filtre par recherche
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim();
      result = result.filter(
        (p) =>
          p.title?.toLowerCase().includes(searchLower) ||
          p.title_en?.toLowerCase().includes(searchLower) ||
          p.category_name?.toLowerCase().includes(searchLower)
      );
    }

    // Filtre par catégorie
    if (selectedCategory) {
      result = result.filter((p) => p.category.name === selectedCategory);
    }

    // Filtre par statut
    if (selectedStatus) {
      result = result.filter((p) => p.status === selectedStatus);
    }

    return result;
  }, [postsByProduct, searchTerm, selectedCategory, selectedStatus]);

  const handleDelete = async () => {
    if (!postToDelete?.id) return;
    setIsDeleting(true);
    try {
      await deletePost(postToDelete.id);
      setPostToDelete(null);
      alertSuccess("Article supprimé avec succès");
    } catch (err) {
      alertError("Erreur lors de la suppression");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteMany = async () => {
    if (!itemsToDelete || itemsToDelete.length === 0) return;
    try {
      await deleteManyPosts(itemsToDelete);
      setItemsToDelete(null);
      alertSuccess(`${itemsToDelete.length} article(s) supprimé(s)`);
    } catch (err) {
      alertError("Erreur lors de la suppression multiple");
    }
  };

  const clearFilters = () => {
    setSelectedCategory("");
    setSelectedStatus("");
    setSearchTerm("");
  };

  const hasActiveFilters = selectedCategory || selectedStatus || searchTerm;

  // Options pour les filtres
  const categoriesOptions = [...new Set(posts.map((p) => p.category?.name).filter(Boolean))];

  const statusOptions = [
    { value: "published", label: "Publié" },
    { value: "draft", label: "Brouillon" },
    { value: "archived", label: "Archivé" },
  ];

  const menuItems: Menu[] = [
    {
      label: "Ajouter un article",
      icon: FileText,
      onClick: () => {
        navigate(`/${role}/blog/posts/new`);
        setIsAddMenuOpen(false);
      },
    },
    {
      label: "Gérer les catégories",
      icon: Tags,
      onClick: () => {
        setModalOpen(true);
        setIsAddMenuOpen(false);
      },
    },
  ];

  return (
    <PageListLayout
      title="Gestion des articles"
      description={`${filteredPosts.length} article${filteredPosts.length > 1 ? "s" : ""} affiché${filteredPosts.length > 1 ? "s" : ""}`}
      actions={
        <FlexContainer className="animate-fade-in-up w-full">
          <button
            onClick={() => setIsAddMenuOpen(true)}
            className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-primary to-primary/80 text-white rounded-xl"
          >
            <Plus size={18} /> Ajouter
          </button>

          <button
            onClick={() => {
              navigate(`/${role}/blog/posts/categories`);
            }}
            className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-primary to-primary/80 text-white rounded-xl"
          >
            <Tags size={18} /> Les categories
          </button>

          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 rounded-xl transition-all ${showFilters ? "bg-primary text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
          >
            <Filter size={18} /> Filtrer
            {hasActiveFilters && <span className="ml-1 w-2 h-2 bg-red-500 rounded-full" />}
          </button>
        </FlexContainer>
      }
      menu={{
        isOpen: isAddMenuOpen,
        onClose: () => setIsAddMenuOpen(false),
        title: "Gestion des articles",
        icon: <Plus className="text-primary" size={20} />,
        items: menuItems,
      }}
    >
      {/* Indicateur de filtre produit */}
      {selectedProduct && selectedProduct !== 'all' && (
        <div className="mb-4 p-3 bg-blue-50 rounded-xl border border-blue-200 text-sm text-blue-700">
          Filtré par produit : <span className="font-semibold">{selectedProduct}</span>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-5">
          <p className="text-xs font-semibold text-blue-700/70">Total articles</p>
          <p className="text-2xl font-bold text-blue-700">{stats.total}</p>
        </div>
        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-5">
          <p className="text-xs font-semibold text-green-700/70">Publiés</p>
          <p className="text-2xl font-bold text-green-700">{stats.published}</p>
        </div>
        <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-5">
          <p className="text-xs font-semibold text-orange-700/70">Brouillons</p>
          <p className="text-2xl font-bold text-orange-700">{stats.draft}</p>
        </div>
        <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-5">
          <p className="text-xs font-semibold text-red-700/70">Archivés</p>
          <p className="text-2xl font-bold text-red-700">{stats.archived}</p>
        </div>
      </div>

      {/* Recherche */}
      <div className="relative mb-4">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Rechercher un article..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl"
        />
      </div>

      {/* Filtres */}
      {showFilters && (
        <div className="bg-gray-50 rounded-xl p-4 mb-4 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-medium text-gray-700">Filtres</h3>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="text-sm text-red-600 hover:text-red-700 flex items-center gap-1"
              >
                <X size={14} /> Effacer tout
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Filtre par catégorie */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie</label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="">Toutes les catégories</option>
                {categoriesOptions.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>

            {/* Filtre par statut */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="">Tous les statuts</option>
                {statusOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Liste */}
      <div>
        <PostsList
          posts={filteredPosts}
          selectable={true}
          onAction={(p) => setSelectedPost(p)}
          selectActions={[
            {
              label: "Supprimer",
              onClick: (selected) => setItemsToDelete(selected.map((i) => i.id)),
              className: "bg-red-600 text-white",
            },
          ]}
        />
      </div>

      {/* Menu modal */}
      {selectedPost && (
        <MenuModal
          title={selectedPost.title}
          isOpen={!!selectedPost}
          onClose={() => setSelectedPost(null)}
          icon={<FileText className="text-primary" size={20} />}
          menu={[
            {
              label: "Voir détails",
              icon: Search,
              onClick: () => navigate(`/${role}/blog/posts/details?id=${selectedPost.id}`, { state: selectedPost }),
            },
            {
              label: "Modifier",
              icon: FileText,
              onClick: () => navigate(`/${role}/blog/posts/update?id=${selectedPost.id}`, { state: selectedPost }),
            },
            {
              label: "Supprimer",
              icon: MoreVertical,
              onClick: () => {
                setPostToDelete(selectedPost);
                setSelectedPost(null);
              },
            },
          ]}
        />
      )}

      {/* Modals suppression */}
      <DeleteConfirmationModal
        isOpen={!!postToDelete}
        onClose={() => setPostToDelete(null)}
        onConfirm={handleDelete}
        title="Supprimer l'article"
        message={`Supprimer "${postToDelete?.title}" ?`}
        confirmText={isDeleting ? "Suppression..." : "Supprimer"}
      />
      <DeleteConfirmationModal
        isOpen={!!itemsToDelete && itemsToDelete.length > 0}
        onClose={() => setItemsToDelete(null)}
        onConfirm={handleDeleteMany}
        title="Suppression multiple"
        message={`Supprimer ${itemsToDelete?.length} article(s) ?`}
      />

      {modalOpen && (
        <CategorieModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          mode={"create"}
          onSuccess={() => {
            setModalOpen(false);
          }}
        />
      )}
    </PageListLayout>
  );
}